import { type ReactiveController, type ReactiveElement } from "lit";

/**
 * Reactive controller for observing slot content with automatic state management.
 *
 * Features:
 * - Reactive `hasContent` property that automatically triggers re-renders
 * - Dual detection: both slot assigned nodes and light DOM fallback
 * - Automatic lifecycle management and cleanup
 * - Debounced updates to prevent race conditions
 * - Seamless integration with Lit's reactive system
 *
 * @example
 * ```typescript
 * class MyModal extends LitElement {
 *   private footerSlot = new SlotContentController(this, 'footer-button-text');
 *   private headerSlot = new SlotContentController(this, 'header');
 *
 *   render() {
 *     return html`
 *       ${this.footerSlot.hasContent
 *         ? html`<div class="footer"><slot name="footer-button-text"></slot></div>`
 *         : ''}
 *     `;
 *   }
 * }
 * ```
 */
export class SlotContentController implements ReactiveController {
  /**
   * Static debouncing mechanism to prevent race conditions when multiple
   * SlotContent controllers exist on the same component.
   */
  private static pendingUpdates = new WeakSet<ReactiveElement>();

  private slotChangeListener?: () => void;
  private _hasContent = false;

  /**
   * Creates a new SlotContent controller
   *
   * @param host - The Lit element that owns this controller (must have shadow DOM)
   * @param slotName - The name of the slot to observe for content
   */
  constructor(
    private host: ReactiveElement,
    private slotName: string,
  ) {
    this.host.addController(this);
  }

  /**
   * Whether the slot currently has content
   * This property is reactive and will trigger re-renders when it changes
   */
  get hasContent(): boolean {
    return this._hasContent;
  }

  hostConnected(): void {
    this.attachSlotChangeListener();
  }

  hostUpdated(): void {
    // Check content after each update in case slot assignment changed
    this.checkSlotContent();
    // Also try to attach slot change listener in case shadow DOM just became available
    this.attachSlotChangeListener();
  }

  hostDisconnected(): void {
    this.cleanup();
  }

  /**
   * Attempts to attach a slot change listener to the shadow DOM slot
   * This is called multiple times to handle cases where shadow DOM isn't ready yet
   */
  private attachSlotChangeListener(): void {
    // Only attach if we don't already have a listener and shadow DOM exists
    if (this.slotChangeListener || !this.host.shadowRoot) {
      return;
    }

    const shadowSlot = this.host.shadowRoot.querySelector(`slot[name="${this.slotName}"]`) as HTMLSlotElement;
    if (shadowSlot) {
      this.slotChangeListener = () => {
        this.checkSlotContent();
      };

      shadowSlot.addEventListener("slotchange", this.slotChangeListener);
    }
  }

  /**
   * Performs the actual slot content detection using dual method approach
   */
  private checkSlotContent(): void {
    let hasContent = false;

    try {
      // Method 1: Check assigned nodes from shadow DOM slot
      const shadowSlot = this.host.shadowRoot?.querySelector(`slot[name="${this.slotName}"]`) as HTMLSlotElement;
      if (shadowSlot) {
        const assignedNodes = shadowSlot.assignedNodes();
        hasContent = assignedNodes.some((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return (node.textContent || "").trim().length > 0;
          }
          return true; // Element nodes are considered content
        });
      }

      // Method 2: Fallback - check light DOM for elements with matching slot attribute
      if (!hasContent) {
        const lightDomElements = this.host.querySelectorAll(`[slot="${this.slotName}"]`);
        hasContent =
          lightDomElements.length > 0 &&
          Array.from(lightDomElements).some((el) => (el.textContent || "").trim().length > 0);
      }
    } catch {
      hasContent = false;
    }

    // Only update and trigger re-render if content state actually changed
    if (hasContent !== this._hasContent) {
      this._hasContent = hasContent;
      this.requestHostUpdate();
    }
  }

  /**
   * Requests a host update with debouncing to prevent race conditions
   * when multiple SlotContent controllers exist on the same component.
   */
  private requestHostUpdate(): void {
    // Only schedule update if not already pending for this host
    if (!SlotContentController.pendingUpdates.has(this.host)) {
      SlotContentController.pendingUpdates.add(this.host);

      // Use requestAnimationFrame to sync with browser's rendering cycle
      requestAnimationFrame(() => {
        SlotContentController.pendingUpdates.delete(this.host);
        this.host.requestUpdate();
      });
    }
  }

  /**
   * Cleans up all observers, listeners, and timeouts
   */
  private cleanup(): void {
    // Clean up slot change listener
    if (this.slotChangeListener && this.host.shadowRoot) {
      const shadowSlot = this.host.shadowRoot.querySelector(`slot[name="${this.slotName}"]`) as HTMLSlotElement;
      if (shadowSlot) {
        shadowSlot.removeEventListener("slotchange", this.slotChangeListener);
      }
      this.slotChangeListener = undefined;
    }

    this._hasContent = false;
  }
}
